import { useState, useMemo, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  TrendingUp, TrendingDown, Sparkles, AlertTriangle,
  CheckCircle2, XCircle, Eye, Loader2, RefreshCw,
  Info
} from 'lucide-react';
import { format, isSameDay } from 'date-fns';
import FormulaReveal, { HOTEL_FORMULAS } from '@/components/ui/FormulaReveal';
import { useAIInsight } from '@/hooks/useAIInsight';
import { useDashboard } from '@/contexts/DashboardContext';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { formatCurrency, formatDateMD } from '@/helpers/formatters';
import { has, isNumber } from 'lodash';
import { COLORS } from '@/constants';
import { useCompset } from '@/contexts/CompsetContext';
import { SmartRateTooltip } from './ChartTooltips';
import { usePlans } from '@/contexts/PlanRulesContext';
import FeatureRestrict from './FeatureRestrict';
import DashboardError from '../error/DashboardError';
import RateParitySetup from '../RateParitySetup';



// Get parity color for heatmap with enhanced gradients
const getParityColor = (status, intensity = 1) => {
  switch (status) {
    case 'parity': return `rgba(16, 185, 129, ${0.3 + intensity * 0.5})`;
    case 'higher': return `rgba(59, 130, 246, ${0.3 + intensity * 0.5})`;
    case 'violation': return `rgba(239, 68, 68, ${0.3 + intensity * 0.5})`;
    default: return 'rgba(156, 163, 175, 0.3)';
  }
};

export default function RateParity() {

  const { activeCompset } = useCompset()

  const { rateparity,
    isLoadingRateparity,
    isErrorRateparity,
    isEnabledRateparity,
    officialSiteInfo,
    isLoadingOfficialSiteInfo,
    isErrorOfficialSiteInfo,
    isEnabledOfficialSiteInfo,
    allAiInsights,
  } = useDashboard();

  const [showAll, setShowAll] = useState(false);
  const [chartTimeRange, setChartTimeRange] = useState('30d');
  const { canAccessAiInsights, calculateAiInsightRefreshCount, canAccessRateParity, maxAiRefreshPerSection } = usePlans();

  // Get days count from time range
  const getDaysFromTimeRange = () => {
    switch (chartTimeRange) {
      case '7d': return 7;
      case '14d': return 14;
      case '30d': return 30;
      default: return 7;
    }
  };


  // Calculate parity stats from API data
  const parityStats = useMemo(() => {
    return {
      parity: rateparity?.summary?.parity || 0,
      higher: rateparity?.summary?.ota_higher || 0,
      lower: rateparity?.summary?.ota_lower || 0,
      violations: rateparity?.summary?.violations || 0,
    };
  }, [rateparity]);




  // Get data for selected date
  // const getSelectedDateData = () => {
  //   const dateStr = format(selectedDate, 'yyyy-MM-dd');
  //   return data?.by_date?.find(d => d.date === dateStr) || null;
  // };

  // ALL HOOKS MUST BE BEFORE ANY CONDITIONAL RETURNS
  const aiRateParityData = useMemo(() => ({
    violations: rateparity?.summary?.violations || 0,
    otas: Object.entries(rateparity?.daily_data || {})
      .filter(([date, value]) => {
        return isSameDay(new Date(), new Date(date))
      })
      .flatMap(([date, value]) => {
        return Object.entries(value).map(([name, obj]) => ({
          name,
          status: obj?.status,
          price: obj?.price
        }))
      })
  }), [rateparity]);

  const { insight: aiRateParityInsight, loading: aiRateParityLoading, refetch: refetchRateParityAI, isAIGenerated: isRateParityAIGenerated, isEnabled, enableAiInsight } = useAIInsight(
    'rate_parity',
    aiRateParityData,
    (!isLoadingRateparity && isEnabledRateparity && !!rateparity && canAccessAiInsights())
  );


  const [dailyBreakdown, otaList] = useMemo(() => {

    if (isLoadingRateparity || isErrorRateparity || !rateparity) return [{}, []]

    const sourceData = {}
    const daysToShow = getDaysFromTimeRange(chartTimeRange)

    Object.entries(rateparity?.daily_data)
      .slice(0, daysToShow)
      .map(([key, value]) => sourceData[key] = value)

    const otaListSet = new Set();

    const source = Object.entries(sourceData || {})?.map(([key, value]) => {
      return {
        date: format(new Date(key), 'MMM dd'),
        fullDate: key,
        otas: Object.entries(value)
          ?.map(([key, value]) => {
            const partnerName = value?.is_client == "true" ? "Direct" : key
            return { [partnerName]: value?.price }
          })
      }
    })



    const transformedData = source.map((date) => {
      const chartData = { ...date }
      date?.otas?.forEach(ota => {
        chartData[Object.keys(ota)] = ota[Object.keys(ota)]
        otaListSet.add(Object.keys(ota)[0])
      })

      return chartData
    })


    return [transformedData, Array.from(otaListSet) || []]

  }, [rateparity, chartTimeRange]);



  const [heatmapData, heatmapOtas] = useMemo(() => {

    if (isLoadingRateparity || isErrorRateparity || !rateparity?.daily_data) {
      return [[], []];
    }

    const daysToShow = getDaysFromTimeRange();
    const otaSet = new Set();

    const rows = Object.entries(rateparity.daily_data)
      .map(([date, otas]) => {

        const row = {
          date: format(new Date(date), "MMM d"),
          fullDate: date
        };

        Object.entries(otas).forEach(([otaName, otaData]) => {

          if (otaData.is_client === "true") return;

          row[otaName] = {
            diff: otaData.value,
            status: otaData.status,
            price: otaData.price
          };

          otaSet.add(otaName);

        });

        return row;

      });

    return [rows, Array.from(otaSet)];

  }, [rateparity]);


  if (isLoadingRateparity) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading rate parity data...</p>
        </div>
      </div>
    );
  }

  if (!canAccessRateParity()) {
    return <FeatureRestrict />
  }

  if (isErrorRateparity) {
    return <DashboardError />
  }

  const hasAiInsightData = allAiInsights?.insights?.find(insight => insight?.section === "rate_parity");

  // const hasOfficialSite = officialSiteInfo?.client_ota_availability
  // const partnerOtaList = officialSiteInfo?.other_ota

  // if (!hasOfficialSite) {
  //   return <RateParitySetup
  //     partners={partnerOtaList}
  //   />
  // }

  return (
    <div className="space-y-6 animate-in fade-in duration-500" data-testid="rate-parity">
      {/* Summary Stats with Modern Gradients */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden border-2 border-emerald-500/10 hover:border-emerald-500/30 transition-all hover:shadow-xl group">
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 opacity-50" />
          <CardContent className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 shadow-lg group-hover:scale-110 transition-transform">
                  <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Rate Parity</p>
                  <p className="text-3xl font-bold text-foreground">{parityStats?.parity}</p>
                </div>
              </div>
              <FormulaReveal
                title="Rate Parity Count"
                type="kpi"
                formula="Parity_Count = COUNT(observations WHERE |OTA_Rate - Direct_Rate| / Direct_Rate <= 0.02)"
                description="Number of OTA rate observations within ±2% of your direct rate."
                methodology={`1. Compare each OTA rate to your direct rate
2. Calculate percentage difference
3. Count observations within ±2% tolerance`}
                dataSource="Rate Parity Monitoring"
              >
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-2 border-blue-500/10 hover:border-blue-500/30 transition-all hover:shadow-xl group">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-indigo-500/10 opacity-50" />
          <CardContent className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg group-hover:scale-110 transition-transform">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">OTA Higher</p>
                  <p className="text-3xl font-bold text-foreground">{parityStats?.higher}</p>
                </div>
              </div>
              <FormulaReveal
                title="OTA Higher Count"
                description="Number of times OTAs are pricing higher than your direct rate."
              >
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-2 border-red-500/10 hover:border-red-500/30 transition-all hover:shadow-xl group">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500/10 to-pink-500/10 opacity-50" />
          <CardContent className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 shadow-lg group-hover:scale-110 transition-transform">
                  <TrendingDown className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">OTA Lower</p>
                  <p className="text-3xl font-bold text-foreground">{parityStats?.lower}</p>
                </div>
              </div>
              <FormulaReveal
                {...HOTEL_FORMULAS.RATE_PARITY_LOWER}
                variables={{
                  'violations': { value: parityStats.lower.toString(), description: 'Current undercuts count' }
                }}
              >
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
        <Card className="relative overflow-hidden border-2 border-amber-500/10 hover:border-amber-500/30 transition-all hover:shadow-xl group">
          <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-orange-500/10 opacity-50" />
          <CardContent className="relative p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 shadow-lg group-hover:scale-110 transition-transform">
                  <AlertTriangle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Undercuts (Today)</p>
                  <p className="text-3xl font-bold text-foreground">{parityStats?.violations}</p>
                </div>
              </div>
              <FormulaReveal
                {...HOTEL_FORMULAS.RATE_PARITY_UNDERCUT}
                variables={{
                  'violations': { value: parityStats.lower.toString(), description: 'Number of rate Undercuts' }
                }}
              >
                <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5 text-muted-foreground hover:text-primary" />
                </Button>
              </FormulaReveal>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rate Parity Over Time Chart - New Addition */}
      <Card className="border-2 border-primary/10 hover:shadow-xl transition-all">
        <CardHeader className="pb-3 sm:pb-6">

          {/* ── Desktop: single row (unchanged)
        ── Mobile:  2 rows
             Row 1: [TrendingUp + Title]
             Row 2: [Description]  ──→  [Tabs + ℹ]
    ─────────────────────────────────────────────────────────────────────── */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">

            {/* Title block */}
            <div className="min-w-0">
              <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary shrink-0" />
                Rate Parity Across 30 days
              </CardTitle>

              {/* Row 2 on mobile: description + controls right-aligned */}
              <div className="flex items-center justify-between mt-1 sm:mt-0 gap-2">

                {/* Description */}
                <CardDescription className="text-xs sm:text-sm">
                  Track your rates vs all OTA platforms
                </CardDescription>

                {/* Controls — mobile only */}
                <div className="flex items-center gap-1.5 sm:hidden shrink-0">
                  <Tabs value={chartTimeRange} onValueChange={setChartTimeRange}>
                    <TabsList className="h-7">
                      <TabsTrigger value="7d" className="text-xs px-2">7D</TabsTrigger>
                      <TabsTrigger value="14d" className="text-xs px-2">14D</TabsTrigger>
                      <TabsTrigger value="30d" className="text-xs px-2">30D</TabsTrigger>
                    </TabsList>
                  </Tabs>
                  <FormulaReveal
                    title="Rate Parity Tracking"
                    type="chart"
                    formula={`For each date and OTA:
  Direct_Rate = Your_Website_Rate
  OTA_Rate = Platform_Rate
  Parity_Status = |OTA_Rate - Direct_Rate| / Direct_Rate <= 0.02`}
                    description="Time series showing how each OTA's rates compare to your direct rate."
                    methodology={`1. Daily scrape of all OTA rates
2. Compare to your direct booking rate
3. Track variations over time
4. Identify patterns and undercuts`}
                    dataSource="OTA Rate Monitoring"
                  >
                    <Button variant="ghost" size="icon" className="h-7 w-7 hover:bg-white/50">
                      <Info className="h-3.5 w-3.5" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>
            </div>

            {/* Controls — desktop only */}
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <Tabs value={chartTimeRange} onValueChange={setChartTimeRange}>
                <TabsList className="h-8">
                  <TabsTrigger value="7d" className="text-xs px-2">7D</TabsTrigger>
                  <TabsTrigger value="14d" className="text-xs px-2">14D</TabsTrigger>
                  <TabsTrigger value="30d" className="text-xs px-2">30D</TabsTrigger>
                </TabsList>
              </Tabs>
              <FormulaReveal
                title="Rate Parity Tracking"
                type="chart"
                formula={`For each date and OTA:
  Direct_Rate = Your_Website_Rate
  OTA_Rate = Platform_Rate
  Parity_Status = |OTA_Rate - Direct_Rate| / Direct_Rate <= 0.02`}
                description="Time series showing how each OTA's rates compare to your direct rate."
                methodology={`1. Daily scrape of all OTA rates
2. Compare to your direct booking rate
3. Track variations over time
4. Identify patterns and undercuts`}
                dataSource="OTA Rate Monitoring"
              >
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5" />
                </Button>
              </FormulaReveal>
            </div>

          </div>
        </CardHeader>

        <CardContent>
          {dailyBreakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={dailyBreakdown}>
                <defs>
                  {otaList.map((ota, idx) => (
                    <linearGradient key={ota} id={`gradient-${ota}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS[idx]} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={COLORS[idx]} stopOpacity={0.2} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" opacity={0.3} />
                <XAxis
                  dataKey="date"
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  stroke="hsl(var(--border))"
                  interval="preserveStart"
                />
                <YAxis
                  tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                  tickFormatter={(v) => formatCurrency(v, activeCompset?.currency)}
                  stroke="hsl(var(--border))"
                />
                <Tooltip
                  content={<SmartRateTooltip currency={activeCompset?.currency} showAll={true} />}
                />
                <Line
                  type="monotone"
                  dataKey="Direct"
                  name="Direct"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  dot={{ fill: '#3b82f6', r: 4 }}
                  activeDot={{ r: 6 }}
                />
                {otaList
                  .filter(ota => ota !== 'Direct')
                  .map((ota, idx) => (
                    <Line
                      key={ota}
                      type="monotone"
                      dataKey={ota}
                      name={ota}
                      stroke={COLORS[idx + 1]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      strokeDasharray={dailyBreakdown.some(d => d.otas?.[ota]?.status === 'lower') ? '5 5' : undefined}
                    />
                  ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[350px] text-muted-foreground">
              No rate parity data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* OTA Performance Summary - New Addition */}


      {/* Date Navigation & Heatmap */}
      <div className="grid grid-cols-1 gap-6">
        {/* Parity Heatmap */}
        <Card className="border-2 border-primary/10 hover:shadow-xl transition-all">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Rate Parity Heatmap
                </CardTitle>
                <CardDescription>Rate comparison across OTAs</CardDescription>
              </div>
              <FormulaReveal
                title="Rate Parity Heatmap"
                type="chart"
                formula={`For each (date, OTA):
  diff = (OTA_Rate - Direct_Rate) / Direct_Rate
  Green (Parity): |diff| <= 2%
  Blue (Higher): diff > 2%
  Red (Violation): diff < -2%`}
                description="Color-coded matrix showing rate parity status across all OTAs and dates."
                dataSource="Daily Rate Scraping"
              >
                <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-white/50">
                  <Info className="h-3.5 w-3.5" />
                </Button>
              </FormulaReveal>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-end gap-4 mb-4 ">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getParityColor("parity") }}
                />
                <span className="text-xs text-muted-foreground">
                  Parity (±2%)
                </span>
              </div>

              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getParityColor("higher") }}
                />
                <span className="text-xs text-muted-foreground">
                  OTA Higher
                </span>
              </div>

              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: getParityColor("violation") }}
                />
                <span className="text-xs text-muted-foreground">
                  Undercuts
                </span>
              </div>
            </div>
            {heatmapData?.length > 0 && heatmapOtas?.length > 0 ? (
              <div className="overflow-x-auto pb-4">
                <div className="min-w-max">
                  {/* Header Row */}
                  <div className="flex items-center gap-1 mb-2">
                    <div className="w-24 text-xs font-medium text-muted-foreground">
                      OTA
                    </div>
                    {heatmapData?.map(day => (
                      <div
                        key={day?.fullDate}
                        className="w-16 text-center text-xs text-muted-foreground"
                      >
                        {formatDateMD(day?.fullDate)}
                      </div>
                    ))}

                  </div>

                  {heatmapOtas?.map(ota => (
                    <div
                      key={ota}
                      className="flex items-center gap-1 mb-1"
                    >
                      <div
                        className="w-24 text-xs truncate"
                        title={ota}
                      >
                        {ota}
                      </div>

                      {heatmapData?.map(day => {
                        const otaData = day?.[ota];
                        const status = otaData?.status || "unknown";
                        return (
                          <div
                            key={`${day?.fullDate}-${ota}`}
                            className="w-16 h-8 rounded flex items-center justify-center text-xs font-medium cursor-pointer hover:ring-2 ring-primary/50 transition-all"
                            style={{
                              backgroundColor: getParityColor(status)
                            }}
                            title={
                              otaData
                                ? `${ota} • ${formatCurrency(otaData?.price, activeCompset?.currency)} • ${otaData?.diff?.toFixed(1)}%`
                                : "No data"
                            }
                          >
                            {isNumber(otaData?.diff)
                              ? `${otaData?.diff > 0 ? "+" : ""}${otaData?.diff?.toFixed(0)}%`
                              : "-"}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

            ) : (

              <div className="text-center py-8 text-muted-foreground">
                No heatmap data available
              </div>

            )}

          </CardContent>
        </Card>
      </div>

      {/* AI Insight */}
      {
        canAccessAiInsights() && (
          <Card className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 border-primary/20" data-testid="ai-rate-parity-card">
            <CardContent className="p-4 sm:p-6">

              {/* ── Header ──────────────────────────────────────────────────────────────
        Mobile:  2 rows
          Row 1: [Icon + Title + Badge]
          Row 2:             justify-end → [Refresh/Generate] [x/x left] [ℹ]
        Desktop: 1 row
          [Icon + Title + Badge] ─── flex-1 ─── [Refresh/Generate] [x/x left] [ℹ]
    ─────────────────────────────────────────────────────────────────────────── */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">

                {/* Left: Icon + Title + Badge */}
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="p-2.5 sm:p-3 rounded-xl bg-accent/10 shrink-0">
                    {aiRateParityLoading ? (
                      <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 text-accent animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-accent" />
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <h3 className="font-semibold text-foreground text-base sm:text-lg leading-tight">AI Rate Parity Analysis</h3>
                    {isRateParityAIGenerated && (
                      <Badge variant="outline" className="text-xs bg-purple-500/10 text-purple-600 border-purple-500/30">
                        Prowen AI
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Right: Actions — justify-end on mobile, inline on desktop */}
                <div className="flex items-center justify-end gap-2 shrink-0">
                  {(calculateAiInsightRefreshCount("rate_parity") && (isEnabled || hasAiInsightData))
                    ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 h-8 px-2 sm:px-3 shrink-0"
                        onClick={() => {
                          enableAiInsight()
                          refetchRateParityAI("regenerate")
                        }}
                        disabled={aiRateParityLoading}
                        title="Refresh AI Analysis"
                      >
                        <RefreshCw className={`h-4 w-4 shrink-0 ${aiRateParityLoading ? 'animate-spin' : ''}`} />
                        <span className="text-xs sm:hidden">Refresh</span>
                      </Button>
                    )
                    : null}
                  {(!isEnabled && !hasAiInsightData) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 h-8 shrink-0"
                      onClick={enableAiInsight}
                    >
                      <span className="text-xs">Generate AI Insights</span>
                    </Button>
                  )}
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {`${calculateAiInsightRefreshCount("rate_parity")} / ${maxAiRefreshPerSection} left`}
                  </span>
                  <FormulaReveal {...HOTEL_FORMULAS.RATEPARITY_AI}>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                      <Info className="h-4 w-4 text-muted-foreground hover:text-primary" />
                    </Button>
                  </FormulaReveal>
                </div>
              </div>

              {/* Body */}
              <p className="text-sm font-medium  leading-relaxed whitespace-pre-wrap break-words">
                {aiRateParityLoading
                  ? 'Analyzing rate parity with Prowen AI...'
                  : aiRateParityInsight
                    ? aiRateParityInsight
                    : hasAiInsightData
                      ? hasAiInsightData?.insight
                      : null
                }
              </p>

            </CardContent>
          </Card>
        )
      }
    </div>
  );
}
